import React, { useMemo } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

/* ── Étoiles locales ── */
const StarsWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;
`;

const StarDot = styled.div`
  position: absolute;
  border-radius: 50%;
  background: white;
`;

function SectionStars({ count = 120 }) {
  const stars = useMemo(() => {
    const arr = [];
    for (let i = 0; i < count; i++) {
      const size = Math.random() * 2 + 0.5;
      arr.push({
        id: i,
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        width: size,
        height: size,
        opacity: Math.random() * 0.45 + 0.08,
        animDelay: Math.random() * 6,
        animDuration: Math.random() * 4 + 3,
      });
    }
    return arr;
  }, [count]);

  return (
    <StarsWrapper>
      {stars.map(s => (
        <StarDot
          key={s.id}
          style={{
            left: s.left,
            top: s.top,
            width: s.width,
            height: s.height,
            opacity: s.opacity,
            animation: `twinkle ${s.animDuration}s ease-in-out ${s.animDelay}s infinite alternate`,
          }}
        />
      ))}
    </StarsWrapper>
  );
}

const AboutContainer = styled.section`
  padding: 6rem 0;
  background: linear-gradient(145deg, #1b4570 0%, #1e4976 50%, #205d8a 100%);
  position: relative;
  overflow: hidden;
  scroll-margin-top: 70px;
  
  @media (max-width: 768px) {
    padding: 4rem 0;
    scroll-margin-top: 60px;
  }
  
  @media (max-width: 480px) {
    padding: 3rem 0;
  }
  
  .dark-mode & {
    background: #0e1420;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 4rem;
  padding: 0 2rem;
  
  @media (max-width: 768px) {
    margin-bottom: 3rem;
    padding: 0 1.5rem;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 2rem;
    padding: 0 1rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: var(--secondary);
  position: relative;
  display: inline-block;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.7rem;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 50px;
    height: 3px;
    background-color: var(--secondary);
    
    .dark-mode & {
      background-color: var(--secondary);
    }
  }
`;

const SectionDescription = styled.p`
  color: rgba(255, 255, 255, 0.75);
  font-size: 1.1rem;
  line-height: 1.6;
`;

const AboutContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
  
  @media (max-width: 768px) {
    padding: 0 1.5rem;
    gap: 2rem;
  }
  
  @media (max-width: 480px) {
    padding: 0 1rem;
  }
`;

const AboutImage = styled.div`
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
    box-shadow: var(--shadow-lg);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: -20px;
    left: -20px;
    width: 100%;
    height: 100%;
    border: 5px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    z-index: -1;
  }
  
  @media (max-width: 992px) {
    height: 400px;
  }
`;

const AboutInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const AboutTitle = styled.h3`
  font-size: 2rem;
  margin-bottom: 1.5rem;
  color: var(--white);
`;

const AboutText = styled.p`
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 2rem;
  line-height: 1.8;
`;

const AboutSection = () => {
  return (
    <AboutContainer id="a-propos">
      <SectionStars count={120} />
      <SectionHeader>
        <SectionTitle className="section-title">À Propos</SectionTitle>
      </SectionHeader>
      
      <AboutContent>
        <motion.div
          initial={{ opacity: 1, x: 0 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <AboutImage>
            <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80" alt="Notre équipe" />
          </AboutImage>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 1, x: 0 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <AboutInfo>
            <AboutTitle>Développeur web passionné par l'innovation</AboutTitle>
            <AboutText>
              GVW-Tech est une entreprise spécialisée dans le développement web, fondée par un développeur passionné par les technologies modernes et l'innovation.
            </AboutText>
            <AboutText>
              Notre mission est de créer des solutions web sur mesure qui répondent parfaitement aux besoins de nos clients, tout en offrant une expérience utilisateur exceptionnelle et des performances optimales.
            </AboutText>
          </AboutInfo>
        </motion.div>
      </AboutContent>
    </AboutContainer>
  );
};

export default AboutSection;
